import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Swap useReducer with XState – CodeWithSwiz 13",
  "description": "In Refactoring a useReducer to XState we talked about a 5 step process to turn a reducer into an XState state machine. Today answers \"Ok now what?\"",
  "categories": "CodeWithSwiz, Technical, XState, Livecoding",
  "published": "2020-10-22T00:00:00.000Z",
  "hero": "./img/screenshot-1603379415789.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`In `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/refactoring-a-usereducer-to-xstate-pt1-codewithswiz-11"
      }}>{`Refactoring a useReducer to XState, pt1`}</a>{` we talked about a 5 step process to turn a reducer into an `}<a parentName="p" {...{
        "href": "https://xstate.js.org/"
      }}>{`XState state machine`}</a>{`. Today answers `}<em parentName="p">{`"Ok now what?"`}</em></p>
    <p><em parentName="p">{`CodeWithSwiz is a twice-a-week live show. Like a podcast with video and fun hacking. Focused on experiments. `}<a parentName="em" {...{
          "href": "https://youtube.com/swizecteller"
        }}>{`Join live Wednesdays and Sundays`}</a></em></p>
    <lite-youtube {...{
      "videoid": "J_ssczSeei4",
      "videostartat": "0"
    }}></lite-youtube>
    <p><a parentName="p" {...{
        "href": "https://github.com/Swizec/useAuth"
      }}>{`useAuth`}</a>{` relies on the `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/wormhole-state-management"
      }}>{`Wormhole state management`}</a>{` principle. Heck, that's where I first figured it out!`}</p>
    <p>{`This has made lots of people very confused. Me included. Come back 6 months later trying to fix a bug and why is there a `}<inlineCode parentName="p">{`dispatch({ type: 'startAuthenticating' })`}</inlineCode>{` in 5 different places wtf??`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media1.giphy.com/media/4JVTF9zR9BicshFAb7/giphy-loop.mp4?cid=7cf28b0fcp9s91xgye61g2bbfqaxux1bvzcn7iufitdkmq3h&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p><em parentName="p">{`"Don't invent new state management schemes, got it"`}</em>{` right? No. Don't make your core state messy and confusing 😉`}</p>
    <h2 {...{
      "id": "a-primer-on-how-useauth-makes-wormholes"
    }}>{`A primer on how useAuth makes wormholes`}</h2>
    <p>{`Wormhole state management gives you global state without the overhead. You add `}<inlineCode parentName="p">{`useAuth`}</inlineCode>{` to your component and magic happens.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Omg I just opensourced the simplest way to add authentication to your React app.`}<br parentName="p"></br><br parentName="p"></br>{`Handles everything for you. Users, login forms, redirects, sharing state between components. Everything`}<br parentName="p"></br><br parentName="p"></br>{`👉 `}<a parentName="p" {...{
          "href": "https://t.co/cBUWGp5Fz5"
        }}>{`https://t.co/cBUWGp5Fz5`}</a><br parentName="p"></br><br parentName="p"></br>{`github 👉 `}<a parentName="p" {...{
          "href": "https://t.co/eXqB6RerLb"
        }}>{`https://t.co/eXqB6RerLb`}</a><br parentName="p"></br><br parentName="p"></br>{`try 👉 `}<a parentName="p" {...{
          "href": "https://t.co/tl7IHTufGq"
        }}>{`https://t.co/tl7IHTufGq`}</a>{` `}<a parentName="p" {...{
          "href": "https://t.co/eH5lSFIzKq"
        }}>{`pic.twitter.com/eH5lSFIzKq`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1159865098556432384"
      }}>{`August 9, 2019`}</a></blockquote>
    <p>{`Internally `}<inlineCode parentName="p">{`useAuth`}</inlineCode>{` connects to a react context holding your authentication state. Info about the user, their current state, faux cookies, and returns helper methods.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "800px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/7de880f2edb58ddbd8b88e85d3d5d861/5a190/How-useAuths-wormhole-state-works3g0064.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "79.37219730941703%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'318\\'%20viewBox=\\'0%200%20400%20318\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M62%2051a135%20135%200%2001-15%202h-2v30c0%2028%200%2030%202%2030h3c1-2%2027-3%2059-3l27-1v3l1%203%201-4c0-3%200-4%203-5%202%200%203-1%203-2h-3l-3%201%201-18V50l-1%201H62m-2%203H50c-3%200-3%202-4%2026%200%2026%200%2033%203%2031%201-2%2025-3%2060-3l24-1c3-1%204-6%204-34l1-18-7-2c-9-1-61-1-71%201m1%207c-3%202-3%203%201%204%202%202%203%203%200%203-1%201-2%200-3-1h-2c-1%204%204%205%208%203%203-1%202-3-1-6l-1-2c6-1%208%200%208%201-1%203%200%205%201%206l1-3%202-3c2%200%202%200%200-2-3-2-11-3-14%200m92%207l-5%204-4%204c0%203%204%205%208%205l6%201%203%201c2-1-5-4-9-4l-4-1c-10-3%2024-1%2040%203%2014%204%2017%203%204%200-12-4-19-5-32-6l-11-1%204-3%202-4-2%201M81%2085l-1%204h-4l-1-1%201-1%201-2c0-2-5-1-6%201%200%203%204%205%206%205%201-1%202%200%202%201%200%202%202%201%203-1%200-2%202-2%208%200l5%201h2l2%201-1-4-3-3-10%201c-2%200-2%200-1-2v-3l-3%203m181-2v2l-3%204-1-2c-1-4-4-4-5%200-1%202-1%202-2%201%200-3-3-5-6-5s-3%200-4%204c-2%206%2010%207%2013%201%201-2%202-2%202-1%200%204%203%205%207%200l3-3v8l2-4c0-3%201-4%202-4l2-1h-10M89%20125v3l1%2011%201%2012c1%202%2014%202%2027%200l20-2c10%200%2011%200%2011%202%200%203%202%202%203-2l5-12c7-12%208-12-33-11a193%20193%200%2001-35-1m16%203H92v22h3c3%201%207%201%2025-2l20-1h10l4-9%205-9-3-1h-51m207%2022c-5%203-6%205-4%205l3%202v2l-2-2c-1-2-3%200-3%202s3%203%206%203c2-1%203-4%200-7-1-2-1-2%201-2h2l1-2v4l1%204%201-3c0-3%201-3%203-3%204%200%204-1%200-3s-5-2-9%200m-23%207l2%207c6%2024-2%2066-17%2083-5%206-6%206-6%201l-1-5v10c0%203%204%202%209-2l5-4%203-1c2-2%200-3-3-1s-3%201%200-5c10-16%2016-51%2011-73-1-7-3-13-3-10m-168%201l-17%202-9%202-1%205c-1%207-3%2014-5%2014l-1%201%204%201%2044-4c11%201%2014%202%206%203-9%202-15%203-14%204l11-1c13-3%2063-4%2063-2l-3%206-4%209-2%205-31%201-30%201v-5l1-9v-3c-2%200-3%203-3%2011%200%209%200%209%2011%208a341%20341%200%200153-1l2-4%205-12c6-11%207-10-24-10l-26%201%202-6%203-8c3-5%203-7-2-8s-24-2-33-1m5%202l-29%204a312%20312%200%2000-3%2017c8-3%2050-5%2054-2%201%201%201%201%202-1a297%20297%200%20015-16c-1-2-21-3-29-2m41%2055c-4%200-4%200-4%203%202%208%202%2014%201%2016-1%201-1%201%201%201%208%202%2019%203%2033%203s17%200%2020%202c5%202%205%202%208-9%202-10%202-12-1-14-4-2-46-4-58-2m2%202c-3%200-3%200-3%204l1%208v5l7%201%2024%201%2020%201%204%202%202-9c2-11%202-11-5-12-6-1-46-2-50-1m-35%2017h-28c-26%200-32%200-32%202l30%201%2030%201c0%202-4%2013-6%2015s-3%202-11%201H74v-7c0-7-1-12-3-10v2l1%208c0%209-1%209%2018%209h27l10%201%202-3c3-3%209-18%207-20h-2m88%2024c-21%201-25%201-24%202h39c48-2%2047-2%2045%2012l-2%209a1050%201050%200%2001-85-1c0%203%205%203%2037%203a656%20656%200%200151-1l1-6c4-20%204-20-62-18m-85%205a469%20469%200%2001-38%205%20970%20970%200%200143-3l5-1v12h-8c-13%201-26%204-33%206-8%202-12%203-12%200-1-2%200-11%202-11s1-2-1-3-3%201-4%208c-1%2011%201%2012%2020%207l25-4h13v-8c0-7-2-10-4-10l-8%202\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/7de880f2edb58ddbd8b88e85d3d5d861/ca0a1/How-useAuths-wormhole-state-works3g0064.webp 223w", "/static/7de880f2edb58ddbd8b88e85d3d5d861/75680/How-useAuths-wormhole-state-works3g0064.webp 445w", "/static/7de880f2edb58ddbd8b88e85d3d5d861/d00b9/How-useAuths-wormhole-state-works3g0064.webp 800w"],
                "sizes": "(max-width: 800px) 100vw, 800px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/7de880f2edb58ddbd8b88e85d3d5d861/e92b6/How-useAuths-wormhole-state-works3g0064.png 223w", "/static/7de880f2edb58ddbd8b88e85d3d5d861/e66bf/How-useAuths-wormhole-state-works3g0064.png 445w", "/static/7de880f2edb58ddbd8b88e85d3d5d861/5a190/How-useAuths-wormhole-state-works3g0064.png 800w"],
                "sizes": "(max-width: 800px) 100vw, 800px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/7de880f2edb58ddbd8b88e85d3d5d861/5a190/How-useAuths-wormhole-state-works3g0064.png",
                "alt": "How useAuth's wormhole state works",
                "title": "How useAuth's wormhole state works",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`How useAuth's wormhole state works`}</figcaption>{`
  `}</figure></p>
    <p>{`That happens in a couple steps:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`// src/AuthProvider.tsx

export const AuthContext = createContext<AuthContextState>({
  state: getDefaultState(),
  dispatch: () => {},
  auth0: null,
  callback_domain: "http://localhost:8000",
  customPropertyNamespace: "http://localhost:8000",
  navigate: (path: string) => {},
})
`}</code></pre>
    <p>{`This creates a new context with default values. `}<inlineCode parentName="p">{`state`}</inlineCode>{` is the application state we're managing, `}<inlineCode parentName="p">{`dispatch`}</inlineCode>{` is the changer method, the rest is meta data and configuration. Values we need everywhere that don't change.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`// src/AuthReducer.tsx

// Holds authentication state
const [state, dispatch] = useReducer<React.Reducer<AuthState, AuthAction>>(
  authReducer,
  getDefaultState()
)

const [contextValue, setContextValue] = useState<AuthContextState>({
  state,
  dispatch,
  auth0,
  callback_domain: callbackDomain,
  customPropertyNamespace,
  navigate,
})

// Update context value and trigger re-render
// This patterns avoids unnecessary deep renders
// https://reactjs.org/docs/context.html#caveats
useEffect(() => {
  setContextValue((contextValue: AuthContextState) => ({
    ...contextValue,
    state,
  }))
}, [state])
`}</code></pre>
    <p>{`The provider instantiates a new reducer and gets access to current `}<inlineCode parentName="p">{`state`}</inlineCode>{` and a `}<inlineCode parentName="p">{`dispatch`}</inlineCode>{` method. These and the config metadata are shoved into a new `}<inlineCode parentName="p">{`useState`}</inlineCode>{`.`}</p>
    <p>{`We use an effect to update the context value when reducer `}<inlineCode parentName="p">{`state`}</inlineCode>{` changes. This ensures re-renders happen only when the reducer makes a change.`}</p>
    <p><inlineCode parentName="p">{`useAuth`}</inlineCode>{` wormholes into this state with a `}<inlineCode parentName="p">{`useContext`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`// src/useAuth

export const useAuth: useAuthInterface = () => {
    const {
        state,
        dispatch,
        auth0,
        callback_domain,
        navigate,
        customPropertyNamespace
    } = useContext(AuthContext);
`}</code></pre>
    <p>{`Anything inside `}<inlineCode parentName="p">{`useAuth`}</inlineCode>{` now has access to these values. Helper methods can check `}<inlineCode parentName="p">{`state`}</inlineCode>{`, `}<inlineCode parentName="p">{`dispatch`}</inlineCode>{` reducer actions, and look at the meta values.`}</p>
    <p>{`Works great.`}</p>
    <h2 {...{
      "id": "how-xstate-helps-us-improve"
    }}>{`How XState helps us improve`}</h2>
    <p>{`Wormhole state worked great but the reducer was problematic. Convoluted to understand and difficult to use.`}</p>
    <p><inlineCode parentName="p">{`startAuthenticating`}</inlineCode>{` fires twice in the code, so does `}<inlineCode parentName="p">{`stopAuthenticating`}</inlineCode>{`. Why? Both where it doesn't make sense.`}</p>
    <p>{`An XState state machine makes the flow clearer 👇`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/f60fe8dbe624dcd003b884493d3259d3/10c1e/useAuth-state-machineaj20fh.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "34.08071748878923%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'136\\'%20viewBox=\\'0%200%20400%20136\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M11%2026c-1%201%200%204%203%206l2%204v2h44v-3c0-4%202-5%204-2%202%202%2017%202%2018-1l6-1h6l-6-1-7-1c-2-3-16-3-17%200l-2%201-2-1c0-2-3-2-22-2H17l21%201h21v9H17v-3c0-2%200-2%202-1l20%201c18%200%2019%200%2019-2s-1-3-2-1H26l-9-1-1-1-1%201-2-2c1-2-1-4-2-2m149%203l-3%201-2%201h2l2%201c2%203%2016%203%2019%200l3-2-3-1c-3-3-16-3-18%200m121%200h-4c-1-1-1%208-1%2021v22l1-21c0-21%201-23%204-18l11%201%2010-1%203-2-1-1-2-2c-2-2-19-1-21%201m-179%202c-3%200-4%200-4%202l16%201%2016%201h2c1-2%200-4-1-4h-29m115%200c-3%200-4%200-4%202l17%201c15%200%2016%200%2016-2s0-2-1-1h-28m-35%203c0%202%200%202-11%202-9%200-11%200-12%202l-2%201c-1-1-2%200-2%201h2l2%201c1%203%2035%203%2037%201s8-2%2012-1c2%200%202%201%202%2015l1%2016V56c0-18%200-18-8-17l-6-1c-1-2-3-2-7-2-6%200-7%200-7-2l-1-2v2m-60%205h-6c-4%200-5%200-5%202l12%201h23c2-1%203%200%204%201%202%201%203%201%203-2%200-2-1-2-14-2a93%2093%200%2001-17%200m115%200h-6c-4%200-5%200-5%202%201%202%2019%202%2021%200h1l13%201h3c3%201%206%200%206-2l-7-1h-7l-1-1-2%201h-6l-2%201-3-1c-3-1-3-1-3%201h-1c0-2-1-2-1-1m1%208c0%202-1%202%2015%202l14%201h2l2-1%201-1c0-1-2-2-7-2l-20%201-1%201-1-1c0-2%200-2-2-1h-2v-1c-1%200-2%201-1%202m-119%202h-6c-5%200-5%200-5%202l5%201a203%20203%200%200130%200c2-1%203%200%204%201%202%201%203%200%203-2s-1-2-11-2h-20m105%2015c-3%202-1%202%2018%202%2023%200%2022%200%2022-1%200-3-26-3-27-1h-1c0-2-9-2-12%200M84%2096v17H50l-33%201h68V80l-1%2016m-41-5h-6c-4%200-5%201-5%202%200%202%2020%202%2021%200l1-1%201%201c-1%201%202%202%2010%201%2011%200%2011%200%2011-2l-6-1H51a423%20423%200%2001-8%200m-10%207c-3%202-1%204%204%203h31c15%200%2015%200%2015-2l-7-1a3254%203254%200%2001-43%200m0%207l-1%202c0%201%2030%202%2031%201h8c4-2%201-3-10-3l-16%201h-1c0-2-1-2-3-1h-8m168%2019L8%20125h193a7051%207051%200%20000-1\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/f60fe8dbe624dcd003b884493d3259d3/ca0a1/useAuth-state-machineaj20fh.webp 223w", "/static/f60fe8dbe624dcd003b884493d3259d3/75680/useAuth-state-machineaj20fh.webp 445w", "/static/f60fe8dbe624dcd003b884493d3259d3/8d1ba/useAuth-state-machineaj20fh.webp 890w", "/static/f60fe8dbe624dcd003b884493d3259d3/3838e/useAuth-state-machineaj20fh.webp 1335w", "/static/f60fe8dbe624dcd003b884493d3259d3/e11e5/useAuth-state-machineaj20fh.webp 1780w", "/static/f60fe8dbe624dcd003b884493d3259d3/e91b7/useAuth-state-machineaj20fh.webp 2090w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/f60fe8dbe624dcd003b884493d3259d3/e92b6/useAuth-state-machineaj20fh.png 223w", "/static/f60fe8dbe624dcd003b884493d3259d3/e66bf/useAuth-state-machineaj20fh.png 445w", "/static/f60fe8dbe624dcd003b884493d3259d3/4ef49/useAuth-state-machineaj20fh.png 890w", "/static/f60fe8dbe624dcd003b884493d3259d3/4e814/useAuth-state-machineaj20fh.png 1335w", "/static/f60fe8dbe624dcd003b884493d3259d3/701e9/useAuth-state-machineaj20fh.png 1780w", "/static/f60fe8dbe624dcd003b884493d3259d3/10c1e/useAuth-state-machineaj20fh.png 2090w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/f60fe8dbe624dcd003b884493d3259d3/4ef49/useAuth-state-machineaj20fh.png",
                "alt": "useAuth state machine",
                "title": "useAuth state machine",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`useAuth state machine`}</figcaption>{`
  `}</figure></p>
    <p>{`You start as `}<inlineCode parentName="p">{`unauthenticated`}</inlineCode>{` and trigger `}<inlineCode parentName="p">{`LOGIN`}</inlineCode>{`. That moves you to `}<inlineCode parentName="p">{`authenticating`}</inlineCode>{`. From there you can get an `}<inlineCode parentName="p">{`ERROR`}</inlineCode>{`, which puts you in the `}<inlineCode parentName="p">{`error`}</inlineCode>{` state. Or you get `}<inlineCode parentName="p">{`AUTHENTICATED`}</inlineCode>{` and move into the `}<inlineCode parentName="p">{`authenticated`}</inlineCode>{` state.`}</p>
    <p><inlineCode parentName="p">{`LOGOUT`}</inlineCode>{` moves you back to `}<inlineCode parentName="p">{`unauthenticated`}</inlineCode>{`.`}</p>
    <p>{`Makes sense eh?`}</p>
    <p><a parentName="p" {...{
        "href": "https://swizec.com/blog/refactoring-a-usereducer-to-xstate-pt1-codewithswiz-11"
      }}>{`While refactoring`}</a>{`, we made the state machine closely follow the reducer implementation.`}</p>
    <p>{`Means we can do a swap!`}</p>
    <p>{`Change `}<inlineCode parentName="p">{`useReducer`}</inlineCode>{` to `}<inlineCode parentName="p">{`useMachine`}</inlineCode>{` from XState`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`// src/AuthProvider.tsx

// Holds authentication state
const [state, dispatch] = useReducer<React.Reducer<AuthState, AuthAction>>(
    authReducer,
    getDefaultState()
);

👇

// Holds authentication state
const [state, send] = useMachine(authMachine);
`}</code></pre>
    <p>{`Change what goes into context:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`const [contextValue, setContextValue] = useState<AuthContextState>({
    state,
    dispatch,

👇

const [contextValue, setContextValue] = useState<AuthContextState>({
    state: state.context,
    send,
`}</code></pre>
    <p>{`Dispatch becomes send and state becomes the XState context. Yes we're overloading terms and that's unfortunate. State machine context is where we keep our application state.`}</p>
    <p>{`Make the same `}<inlineCode parentName="p">{`state`}</inlineCode>{` fix in the effect:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`useEffect(() => {
    setContextValue((contextValue: AuthContextState) => ({
        ...contextValue,
        state
    }));
}, [state]);

👇

useEffect(() => {
    setContextValue((contextValue: AuthContextState) => ({
        ...contextValue,
        state: state.context
    }));
}, [state]);
`}</code></pre>
    <p>{`And then it's a schlep to go around the codebase and change every `}<inlineCode parentName="p">{`dispatch({ type: X })`}</inlineCode>{` to `}<inlineCode parentName="p">{`send(X)`}</inlineCode>{`.`}</p>
    <p>{`We went from 9 `}<inlineCode parentName="p">{`dispatch()`}</inlineCode>{` calls to 9 `}<inlineCode parentName="p">{`send()`}</inlineCode>{` calls, which doesn't sound like an improvement. 🤔`}</p>
    <p>{`But it's because state machines are strict and we used 2 sends to hydrate from local storage. `}<inlineCode parentName="p">{`useReducer`}</inlineCode>{` let us fake that part.`}</p>
    <p>{`😅`}</p>
    <h2 {...{
      "id": "next-step"
    }}>{`Next step`}</h2>
    <p><a parentName="p" {...{
        "href": "https://twitter.com/davidkpiano"
      }}>{`David`}</a>{`, the creator of XState, says we can stop using a context provider. And that's what we're gonna try next.`}</p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      